import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { coachTeam, partnerAffiliations } from '@/mocks/aboutData';

export default function About() {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50">{t('about.title')}</h1>
          <p className="text-sm md:text-base text-background-50/70 mt-4 max-w-2xl mx-auto">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* History */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{t('about.history')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t('about.historyText1')}</p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t('about.historyText2')}</p>
          </div>
        </div>
      </section>

      {/* President Quote */}
      <section className="py-12 md:py-16 bg-primary-900">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden mx-auto mb-6 border-3 border-accent-500">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20an%20African%20businessman%20in%20his%2040s%2C%20wearing%20a%20dark%20blazer%20and%20white%20shirt%2C%20confident%20expression%2C%20warm%20lighting%2C%20luxury%20corporate%20portrait%20style%2C%20clean%20background%2C%20president%20of%20a%20prestigious%20sports%20club&width=200&height=200&seq=president-portrait-01&orientation=squarish"
              alt="Baby Olivier Pandja — Président du TCD"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <blockquote className="font-heading text-xl md:text-2xl italic text-background-50/90 max-w-3xl mx-auto leading-relaxed">
            Notre ambition est de faire du tennis club de Douala le lieu de référence du tennis à Douala et au Cameroun. Nous pratiquons le tennis et assurons sa promotion à travers la détection des futurs champions.
          </blockquote>
          <p className="text-accent-400 font-semibold mt-4">Baby Olivier Pandja</p>
          <p className="text-background-50/50 text-sm">Président du Tennis Club de Douala</p>
        </div>
      </section>

      {/* Team */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about.team')}</h2>
            <p className="text-sm text-foreground-500 mt-2">{t('about.teamSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coachTeam.map((coach) => (
              <div key={coach.id} className="bg-background-100 rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-200/50 transition-all duration-300">
                <div className="h-56 overflow-hidden">
                  <img src={coach.image} alt={coach.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">{coach.name}</h3>
                  <p className="text-xs text-accent-600 font-medium mt-1">{coach.role}</p>
                  <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{coach.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about.values')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { key: 'Excellence', descKey: 'about.valuesExcellenceDesc', icon: 'ri-star-line' },
              { key: 'Formation', descKey: 'about.valuesTrainingDesc', icon: 'ri-graduation-cap-line' },
              { key: 'Communauté', descKey: 'about.valuesCommunityDesc', icon: 'ri-heart-line' },
              { key: 'Innovation', descKey: 'about.valuesInnovationDesc', icon: 'ri-lightbulb-line' },
            ].map((val) => (
              <div key={val.key} className="bg-background-50 rounded-2xl p-5 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className={`${val.icon} text-lg text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{t(`about.values${val.key}`)}</h3>
                <p className="text-xs text-foreground-500">{t(val.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about.partners')}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {partnerAffiliations.map((partner, idx) => (
              <div key={idx} className="bg-background-100 rounded-xl p-4 text-center border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-2">
                  <i className={`${partner.icon} text-accent-600`}></i>
                </div>
                <p className="text-xs text-foreground-600 font-medium">{partner.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-accent-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">{t('about.contactCta')}</h2>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-foreground-900 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t('about.contactBtn')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </a>
        </div>
      </section>

      <Footer />
      <a href="https://wa.me/237687696969" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#22c55e] transition-all duration-300 hover:scale-110 cursor-pointer" aria-label="Contact WhatsApp">
        <i className="ri-whatsapp-line text-2xl md:text-3xl"></i>
      </a>
    </main>
  );
}